import { useLingui } from '@lingui/react/macro';
import { Link, useRouter } from '@tanstack/react-router';
import { Construction } from 'lucide-react';
import { useCallback, type ComponentProps, type ReactNode } from 'react';

import { UNDER_CONSTRUCTION_PATH, underConstructionPath } from '@/shared/config';
import { Alert } from '@/shared/ui/alert';
import { cn } from '@/shared/ui/cn';

/** Abre a tela "Em construção" do caminho do UYZY atual, na mesma aba e dentro da casca. */
export function useOpenUnderConstruction(): (legacyPath: string) => void {
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | null;
  return useCallback(
    (legacyPath: string) => {
      if (router) void router.navigate({ to: UNDER_CONSTRUCTION_PATH, search: { caminho: legacyPath } });
      else window.location.assign(underConstructionPath(legacyPath));
    },
    [router],
  );
}

export type UnderConstructionLinkProps = Omit<ComponentProps<'a'>, 'href' | 'target' | 'rel'> & {
  /** Caminho da tela no UYZY atual (`/ccb/operacoes/123/cessoes`). */
  readonly path: string;
};

/** Link interno para a tela "Em construção" (U15): nunca sai do app nem abre outra aba. */
export function UnderConstructionLink({ path, children, ...props }: UnderConstructionLinkProps) {
  // Bloco fora de um roteador (contrato de bloco, prévia): o mesmo destino como link comum.
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | null;
  if (!router) {
    return (
      <a {...props} href={underConstructionPath(path)}>
        {children}
      </a>
    );
  }
  return (
    <Link {...props} to={UNDER_CONSTRUCTION_PATH} search={{ caminho: path }}>
      {children}
    </Link>
  );
}

export interface UnderConstructionStateProps {
  /** Área, seção ou aba, pequena acima do título. */
  readonly eyebrow?: ReactNode;
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
  /** Dentro de aba ou painel: menor e sem cabeçalho de seção. */
  readonly compact?: boolean;
  readonly className?: string;
}

/** Estado "Em construção": a tela da casca e, compacto, a aba ou o painel que ainda não chegou. */
export function UnderConstructionState({ eyebrow, title, description, actions, compact, className }: UnderConstructionStateProps) {
  const { t } = useLingui();
  const Heading = compact ? 'p' : 'h2';
  return (
    <div
      role={compact ? 'status' : undefined}
      data-slot="under-construction"
      className={cn('flex flex-col items-center justify-center gap-4 text-center', compact ? 'px-4 py-8' : 'px-6 py-14', className)}
    >
      <span
        aria-hidden
        className={cn(
          'inline-flex items-center justify-center rounded-2xl bg-accent-wash text-accent ring-1 ring-accent-line',
          compact ? 'size-12' : 'size-16',
        )}
      >
        <Construction className={compact ? 'size-6' : 'size-8'} strokeWidth={1.75} />
      </span>
      <div className="flex max-w-[420px] flex-col gap-1.5">
        {eyebrow ? <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{eyebrow}</p> : null}
        <Heading className={cn('font-semibold tracking-tight text-foreground', compact ? 'text-base' : 'text-xl')}>{title ?? t`Em construção`}</Heading>
        <p className="text-sm text-muted-foreground">{description ?? t`Estamos trazendo esta área para o novo UYZY.`}</p>
      </div>
      {actions ? <div className="flex flex-wrap items-center justify-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Aba ou seção inteira que ainda não chegou ao novo UYZY, no lugar do antigo "Abrir no UYZY atual". */
export function UnderConstructionPanel({ className, ...props }: Omit<UnderConstructionStateProps, 'compact'>) {
  return (
    <div className={cn('rounded-card border border-border bg-card', className)}>
      <UnderConstructionState compact {...props} />
    </div>
  );
}

/** Parte de uma aba que funciona e que ainda não chegou (assinar, gerir operadores): o aviso em linha. */
export function UnderConstructionNotice({ title, children, className }: { readonly title?: ReactNode; readonly children: ReactNode; readonly className?: string }) {
  const { t } = useLingui();
  return (
    <Alert tone="neutral" icon={Construction} title={title ?? t`Em construção`} className={className}>
      {children}
    </Alert>
  );
}
